import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { SectionWrapper } from 'remote-com-shared/components/ClientLogos';
import {
  RightBG,
  LeftBG,
  LogoEntry,
  Logos,
} from 'remote-com-shared/components/ClientLogos/ClientLogosSlider';
import { AnimatedSection, sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';
import { SwiperSlide } from 'swiper/react';

import { Section as SectionComponent, Label, Title, Description } from './RankSlice';

import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  fromDesktopBreakpoint,
  desktopBreakpoint,
  tabletBreakpoint,
} from '@/src/domains/shared/breakpoints';
import { CountryCardsSwiper, CardsList } from '@/src/views/RemoteRelocation/WorkAnywhere';

const Section = styled(SectionComponent)`
  ${({ $tone }) => {
    switch ($tone) {
      case 'lightPurple':
        return css`
          background: #5700ff;
        `;
      default:
        return css`
          background: var(--colors-darkBlue);
        `;
    }
  }}

  ${Label}, ${Title}, ${Description} {
    color: #fbf9ff;
    text-align: center;
  }

  ${SectionWrapper} {
    background: transparent;
    padding-top: 0;
  }

  ${RightBG}, ${LeftBG} {
    display: none;
  }

  ${LogoEntry} {
    margin-left: 0;
    ${sharedTransition('transform')}
    max-width: 150px;

    &:hover {
      transform: scale(1.2);
    }

    ${desktopBreakpoint(css`
      margin: auto;
    `)}
  }

  .swiper-button-prev,
  .swiper-button-next {
    background-color: rgba(223, 239, 211, 0.5);
  }

  .swiper-button-next {
    &::before {
      background-color: #dfefd3;
    }
  }

  .swiper-slide {
    ${sharedTransition('opacity')}
  }

  .swiper-wrapper {
    display: flex;
    align-items: center;
  }

  ${fromDesktopBreakpoint(css`
    .swiper-slide-duplicate-prev,
    .swiper-slide-active,
    .swiper-slide-prev,
    .swiper-slide-duplicate-active {
      opacity: 0.2;
    }
  `)}

  ${desktopBreakpoint(css`
    .swiper-slide {
      opacity: 0.2;
    }

    .swiper-slide-next,
    .swiper-slide-active,
    .swiper-slide-prev {
      opacity: 1;
    }
  `)}

    ${tabletBreakpoint(css`
    .swiper-slide {
      opacity: 0.2;
    }

    .swiper-slide-active {
      opacity: 1;
    }
  `)}

    .swiper-container {
    padding-bottom: 120px;
  }
`;

export function AwardsSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const { label, title, description, tone } = primary;
  const { fields: rewardImages } = slice;

  return (
    <Section $tone={tone}>
      {label && <Label>{label}</Label>}
      {title && (
        <Title>
          <PrismicRichText field={title} />
        </Title>
      )}
      {description && (
        <Description>
          <PrismicRichText field={description} />
        </Description>
      )}
      <Logos>
        <AnimatedSection
          ComponentName={CountryCardsSwiper}
          loop
          navigation
          grabCursor
          a11y
          centeredSlides
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          slidesPerView={3}
          // slidesPerView: 'auto' in breakpoints is not used because it requires loopedSlides, which breaks styling of edge elements
          breakpoints={{
            768: {
              slidesPerView: 5,
            },
            1060: {
              slidesPerView: 8,
              centeredSlides: false,
            },
          }}
        >
          <CardsList>
            {rewardImages.map(({ logo_image: logo }, index) => {
              const { dimensions: sourceDimensions } = logo;
              const isSvg = /\.svg$/i.test(logo.url);

              // Cut non-svg dimensions in half if 2x resolution
              let dimensions = sourceDimensions;
              const reference2xWidth = 200;
              if (!isSvg && dimensions.width > reference2xWidth) {
                dimensions = {
                  width: dimensions.width / 2,
                  height: dimensions.height / 2,
                  unoptimized: true,
                };
              }
              return (
                <SwiperSlide key={index}>
                  <div>
                    <LogoEntry>
                      <Image src={logo?.url} alt={logo.alt ?? ''} {...dimensions} quality="100" />
                    </LogoEntry>
                  </div>
                </SwiperSlide>
              );
            })}
          </CardsList>
        </AnimatedSection>
      </Logos>
    </Section>
  );
}
